/**
 * Copyright 2018 OpenStack Foundation
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 * http://www.apache.org/licenses/LICENSE-2.0
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 * */

import {
  createAction,
  getRequest,
  postRequest,
  putRequest,
  deleteRequest,
  startLoading,
  stopLoading,
  escapeFilterValue,
  snackbarErrorHandler,
  snackbarSuccessHandler
} from "openstack-uicore-foundation/lib/utils/actions";
import T from "i18n-react/dist/i18n-react";
import {
  getAccessTokenSafely,
  normalizeSelectAllField
} from "../utils/methods";
import {
  DEFAULT_CURRENT_PAGE,
  DEFAULT_ORDER_DIR,
  DEFAULT_PER_PAGE
} from "../utils/constants";
import { normalizePageTemplateModules } from "../utils/page-template";

export const GLOBAL_PAGE_CLONED = "GLOBAL_PAGE_CLONED";
export const RESET_EDIT_PAGE = "RESET_EDIT_PAGE";

export const REQUEST_SPONSOR_MANAGED_PAGES = "REQUEST_SPONSOR_MANAGED_PAGES";
export const RECEIVE_SPONSOR_MANAGED_PAGES = "RECEIVE_SPONSOR_MANAGED_PAGES";
export const RECEIVE_SPONSOR_MANAGED_PAGE = "RECEIVE_SPONSOR_MANAGED_PAGE";
export const SPONSOR_MANAGED_PAGE_ADDED = "SPONSOR_MANAGED_PAGE_ADDED";
export const SPONSOR_MANAGED_PAGE_DELETED = "SPONSOR_MANAGED_PAGE_DELETED";
export const SPONSOR_MANAGED_PAGE_UPDATED = "SPONSOR_MANAGED_PAGE_UPDATED";

export const REQUEST_SPONSOR_CUSTOMIZED_PAGES =
  "REQUEST_SPONSOR_CUSTOMIZED_PAGES";
export const RECEIVE_SPONSOR_CUSTOMIZED_PAGES =
  "RECEIVE_SPONSOR_CUSTOMIZED_PAGES";
export const RECEIVE_SPONSOR_CUSTOMIZED_PAGE =
  "RECEIVE_SPONSOR_CUSTOMIZED_PAGE";
export const SPONSOR_CUSTOMIZED_PAGE_ADDED = "SPONSOR_CUSTOMIZED_PAGE_ADDED";
export const SPONSOR_CUSTOMIZED_PAGE_UPDATED =
  "SPONSOR_CUSTOMIZED_PAGE_UPDATED";
export const SPONSOR_CUSTOMIZED_PAGE_DELETED =
  "SPONSOR_CUSTOMIZED_PAGE_DELETED";
export const SPONSOR_CUSTOMIZED_PAGE_ARCHIVED =
  "SPONSOR_CUSTOMIZED_PAGE_ARCHIVED";
export const SPONSOR_CUSTOMIZED_PAGE_UNARCHIVED =
  "SPONSOR_CUSTOMIZED_PAGE_UNARCHIVED";

export const cloneGlobalPage =
  (pagesIds, sponsorIds, allSponsors) => async (dispatch, getState) => {
    const { currentSummitState } = getState();
    const accessToken = await getAccessTokenSafely();
    const { currentSummit } = currentSummitState;

    dispatch(startLoading());

    const params = {
      access_token: accessToken
    };

    const normalizedEntity = {
      page_template_ids: pagesIds,
      sponsorship_types: sponsorIds,
      apply_to_all_types: allSponsors
    };

    if (allSponsors) {
      delete normalizedEntity.sponsorship_types;
    }

    return postRequest(
      null,
      createAction(GLOBAL_PAGE_CLONED),
      `${window.SPONSOR_PAGES_API_URL}/api/v1/summits/${currentSummit.id}/show-pages/clone`,
      normalizedEntity,
      snackbarErrorHandler
    )(params)(dispatch)
      .then(() => {
        dispatch(
          snackbarSuccessHandler({
            title: T.translate("general.success"),
            html: T.translate("show_pages.global_page_popup.success")
          })
        );
      })
      .finally(() => dispatch(stopLoading()));
  };

export const resetSponsorPage = () => (dispatch) => {
  dispatch(createAction(RESET_EDIT_PAGE)({}));
};

/* ************************************************************************ */
/*         MANAGED PAGES       */
/* ************************************************************************ */

export const getSponsorManagedPages =
  (
    term = "",
    page = DEFAULT_CURRENT_PAGE,
    perPage = DEFAULT_PER_PAGE,
    order = "id",
    orderDir = DEFAULT_ORDER_DIR,
    showArchived = false
  ) =>
  async (dispatch, getState) => {
    const { currentSummitState, currentSponsorState } = getState();
    const { currentSummit } = currentSummitState;
    const {
      entity: { id: sponsorId }
    } = currentSponsorState;
    const accessToken = await getAccessTokenSafely();
    const summitTZ = currentSummit.time_zone.name;
    const filter = [];

    dispatch(startLoading());

    if (term) {
      const escapedTerm = escapeFilterValue(term);
      filter.push(`name=@${escapedTerm},code=@${escapedTerm}`);
    }

    const params = {
      page,
      fields: "id,code,name,kind,modules_count,allowed_add_ons,assigned_type",
      per_page: perPage,
      access_token: accessToken
    };

    filter.push(`is_archived==${showArchived ? 1 : 0}`);

    if (filter.length > 0) {
      params["filter[]"] = filter;
    }

    // order
    if (order != null && orderDir != null) {
      const orderDirSign = orderDir === 1 ? "" : "-";
      params.order = `${orderDirSign}${order}`;
    }

    return getRequest(
      createAction(REQUEST_SPONSOR_MANAGED_PAGES),
      createAction(RECEIVE_SPONSOR_MANAGED_PAGES),
      `${window.SPONSOR_PAGES_API_URL}/api/v1/summits/${currentSummit.id}/sponsors/${sponsorId}/managed-pages`,
      snackbarErrorHandler,
      { order, orderDir, page, perPage, term, showArchived, summitTZ }
    )(params)(dispatch).finally(() => {
      dispatch(stopLoading());
    });
  };

export const getSponsorManagedPage = (pageId) => async (dispatch, getState) => {
  const { currentSummitState } = getState();
  const { currentSummit } = currentSummitState;
  const accessToken = await getAccessTokenSafely();

  dispatch(startLoading());

  const params = {
    access_token: accessToken,
    expand: "modules"
  };

  return getRequest(
    null,
    createAction(RECEIVE_SPONSOR_MANAGED_PAGE),
    `${window.SPONSOR_PAGES_API_URL}/api/v1/summits/${currentSummit.id}/show-pages/${pageId}`,
    snackbarErrorHandler
  )(params)(dispatch).finally(() => {
    dispatch(stopLoading());
  });
};

export const saveSponsorManagedPage =
  (entity) => async (dispatch, getState) => {
    const { currentSummitState, currentSponsorState } = getState();
    const { currentSummit } = currentSummitState;
    const {
      entity: { id: sponsorId }
    } = currentSponsorState;
    const accessToken = await getAccessTokenSafely();

    dispatch(startLoading());

    const params = {
      access_token: accessToken,
      fields: "id,code,name,kind,modules_count,allowed_add_ons"
    };

    if (entity.id) {
      const normalizedEntity = normalizeSponsorManagedPageToCustomize(entity);

      return putRequest(
        null,
        createAction(SPONSOR_CUSTOMIZED_PAGE_UPDATED),
        `${window.SPONSOR_PAGES_API_URL}/api/v1/summits/${currentSummit.id}/sponsors/${sponsorId}/managed-pages/${entity.id}`,
        normalizedEntity,
        snackbarErrorHandler,
        entity
      )(params)(dispatch)
        .then(() => {
          dispatch(
            snackbarSuccessHandler({
              title: T.translate("general.success"),
              html: T.translate("edit_sponsor.pages_tab.managed_page_saved")
            })
          );
        })
        .finally(() => {
          dispatch(stopLoading());
        });
    }

    const normalizedEntity = normalizeSponsorManagedPage(entity);

    return postRequest(
      null,
      createAction(SPONSOR_MANAGED_PAGE_ADDED),
      `${window.SPONSOR_PAGES_API_URL}/api/v1/summits/${currentSummit.id}/sponsors/${sponsorId}/managed-pages`,
      normalizedEntity,
      snackbarErrorHandler
    )(params)(dispatch)
      .then(() => {
        dispatch(
          snackbarSuccessHandler({
            title: T.translate("general.success"),
            html: T.translate("edit_sponsor.pages_tab.managed_page_created")
          })
        );
      })
      .finally(() => {
        dispatch(stopLoading());
      });
  };

export const deleteSponsorManagedPage =
  (pageId) => async (dispatch, getState) => {
    const { currentSummitState, currentSponsorState } = getState();
    const { currentSummit } = currentSummitState;
    const {
      entity: { id: sponsorId }
    } = currentSponsorState;
    const accessToken = await getAccessTokenSafely();
    const params = { access_token: accessToken };

    dispatch(startLoading());

    return deleteRequest(
      null,
      createAction(SPONSOR_MANAGED_PAGE_DELETED)({ pageId }),
      `${window.SPONSOR_PAGES_API_URL}/api/v1/summits/${currentSummit.id}/sponsors/${sponsorId}/managed-pages/${pageId}`,
      null,
      snackbarErrorHandler
    )(params)(dispatch)
      .then(() => {
        dispatch(
          snackbarSuccessHandler({
            title: T.translate("general.success"),
            html: T.translate("show_pages.page_delete_success")
          })
        );
      })
      .finally(() => {
        dispatch(stopLoading());
      });
  };

const normalizeSponsorManagedPage = (entity) => {
  const normalizedEntity = {
    show_page_ids: entity.pages,
    allowed_add_ons: entity.add_ons
  };

  if (normalizedEntity.allowed_add_ons.includes("all")) {
    normalizedEntity.apply_to_all_add_ons = true;
    normalizedEntity.allowed_add_ons = [];
  } else {
    normalizedEntity.allowed_add_ons = entity.add_ons.map((a) => a.id);
    normalizedEntity.apply_to_all_add_ons = false;
  }

  return normalizedEntity;
};

export const normalizeSponsorManagedPageToCustomize = (entity) => {
  const normalizedEntity = {
    ...entity,
    ...normalizeSelectAllField(
      entity.allowed_add_ons,
      "apply_to_all_add_ons",
      "allowed_add_ons"
    )
  };

  normalizedEntity.modules = normalizePageTemplateModules(entity.modules);

  delete normalizedEntity.page_ptr_id;
  delete normalizedEntity.sponsorship_types;
  delete normalizedEntity.summit_id;
  delete normalizedEntity.template_id;
  delete normalizedEntity.modules_count;
  delete normalizedEntity.id;

  return normalizedEntity;
};
/* ************************************************************************ */
/*         CUSTOMIZED PAGES       */
/* ************************************************************************ */

export const getSponsorCustomizedPages =
  (
    term = "",
    page = DEFAULT_CURRENT_PAGE,
    perPage = DEFAULT_PER_PAGE,
    order = "id",
    orderDir = DEFAULT_ORDER_DIR,
    showArchived = false
  ) =>
  async (dispatch, getState) => {
    const { currentSummitState, currentSponsorState } = getState();
    const { currentSummit } = currentSummitState;
    const {
      entity: { id: sponsorId }
    } = currentSponsorState;
    const accessToken = await getAccessTokenSafely();
    const summitTZ = currentSummit.time_zone.name;
    const filter = [];

    dispatch(startLoading());

    if (term) {
      const escapedTerm = escapeFilterValue(term);
      filter.push(`name=@${escapedTerm},code=@${escapedTerm}`);
    }

    const params = {
      page,
      fields:
        "id,code,name,allowed_add_ons,is_archived,modules,allowed_add_ons.type,allowed_add_ons.name,allowed_add_ons.id",
      expand: "allowed_add_ons,modules",
      per_page: perPage,
      access_token: accessToken
    };

    filter.push(`is_archived==${showArchived ? 1 : 0}`);

    if (filter.length > 0) {
      params["filter[]"] = filter;
    }

    // order
    if (order != null && orderDir != null) {
      const orderDirSign = orderDir === 1 ? "" : "-";
      params.order = `${orderDirSign}${order}`;
    }

    return getRequest(
      createAction(REQUEST_SPONSOR_CUSTOMIZED_PAGES),
      createAction(RECEIVE_SPONSOR_CUSTOMIZED_PAGES),
      `${window.SPONSOR_PAGES_API_URL}/api/v1/summits/${currentSummit.id}/sponsors/${sponsorId}/sponsor-pages`,
      snackbarErrorHandler,
      { order, orderDir, page, perPage, term, showArchived, summitTZ }
    )(params)(dispatch).finally(() => {
      dispatch(stopLoading());
    });
  };

export const getSponsorCustomizedPage =
  (pageId) => async (dispatch, getState) => {
    const { currentSummitState, currentSponsorState } = getState();
    const { currentSummit } = currentSummitState;
    const {
      entity: { id: sponsorId }
    } = currentSponsorState;
    const accessToken = await getAccessTokenSafely();

    dispatch(startLoading());

    const params = {
      fields:
        "id,code,name,allowed_add_ons,is_archived,modules,allowed_add_ons.name,allowed_add_ons.id",
      expand: "allowed_add_ons,modules",
      access_token: accessToken
    };

    return getRequest(
      null,
      createAction(RECEIVE_SPONSOR_CUSTOMIZED_PAGE),
      `${window.SPONSOR_PAGES_API_URL}/api/v1/summits/${currentSummit.id}/sponsors/${sponsorId}/sponsor-pages/${pageId}`,
      snackbarErrorHandler
    )(params)(dispatch).finally(() => {
      dispatch(stopLoading());
    });
  };

export const saveSponsorCustomizedPage =
  (entity) => async (dispatch, getState) => {
    const { currentSummitState, currentSponsorState } = getState();
    const { currentSummit } = currentSummitState;
    const {
      entity: { id: sponsorId }
    } = currentSponsorState;
    const summitTZ = currentSummit.time_zone.name;
    const accessToken = await getAccessTokenSafely();

    dispatch(startLoading());

    const normalizedEntity = normalizeSponsorCustomPage(entity, summitTZ);

    const params = {
      access_token: accessToken,
      fields: "id,code,name,kind,modules_count,allowed_add_ons"
    };

    if (entity.id) {
      return putRequest(
        null,
        createAction(SPONSOR_CUSTOMIZED_PAGE_UPDATED),
        `${window.SPONSOR_PAGES_API_URL}/api/v1/summits/${currentSummit.id}/sponsors/${sponsorId}/sponsor-pages/${entity.id}`,
        normalizedEntity,
        snackbarErrorHandler,
        entity
      )(params)(dispatch)
        .then(() => {
          dispatch(
            snackbarSuccessHandler({
              title: T.translate("general.success"),
              html: T.translate("edit_sponsor.pages_tab.custom_page_saved")
            })
          );
        })
        .finally(() => {
          dispatch(stopLoading());
        });
    }

    return postRequest(
      null,
      createAction(SPONSOR_CUSTOMIZED_PAGE_ADDED),
      `${window.SPONSOR_PAGES_API_URL}/api/v1/summits/${currentSummit.id}/sponsors/${sponsorId}/sponsor-pages`,
      normalizedEntity,
      snackbarErrorHandler
    )(params)(dispatch)
      .then(() => {
        dispatch(
          snackbarSuccessHandler({
            title: T.translate("general.success"),
            html: T.translate("edit_sponsor.pages_tab.custom_page_created")
          })
        );
      })
      .finally(() => {
        dispatch(stopLoading());
      });
  };

export const archiveCustomizedPage = (pageId) => async (dispatch, getState) => {
  const { currentSummitState, currentSponsorState } = getState();
  const { currentSummit } = currentSummitState;
  const {
    entity: { id: sponsorId }
  } = currentSponsorState;
  const accessToken = await getAccessTokenSafely();
  const params = { access_token: accessToken };

  dispatch(startLoading());

  return putRequest(
    null,
    createAction(SPONSOR_CUSTOMIZED_PAGE_ARCHIVED)({ pageId }),
    `${window.SPONSOR_PAGES_API_URL}/api/v1/summits/${currentSummit.id}/sponsors/${sponsorId}/sponsor-pages/${pageId}/archive`,
    null,
    snackbarErrorHandler
  )(params)(dispatch)
    .then(() => {
      dispatch(
        snackbarSuccessHandler({
          title: T.translate("general.success"),
          html: T.translate("edit_sponsor.pages_tab.customized_page_archived")
        })
      );
      const { term, showArchived, customizedPages } =
        getState().sponsorPagePagesListState;
      const { currentPage, perPage, order, orderDir } = customizedPages;
      return dispatch(
        getSponsorCustomizedPages(
          term,
          currentPage,
          perPage,
          order,
          orderDir,
          showArchived
        )
      );
    })
    .catch(() => {})
    .finally(() => dispatch(stopLoading()));
};

export const unarchiveCustomizedPage =
  (pageId) => async (dispatch, getState) => {
    const { currentSummitState, currentSponsorState } = getState();
    const { currentSummit } = currentSummitState;
    const {
      entity: { id: sponsorId }
    } = currentSponsorState;
    const accessToken = await getAccessTokenSafely();
    const params = { access_token: accessToken };

    dispatch(startLoading());

    return deleteRequest(
      null,
      createAction(SPONSOR_CUSTOMIZED_PAGE_UNARCHIVED)({ pageId }),
      `${window.SPONSOR_PAGES_API_URL}/api/v1/summits/${currentSummit.id}/sponsors/${sponsorId}/sponsor-pages/${pageId}/archive`,
      null,
      snackbarErrorHandler
    )(params)(dispatch)
      .then(() => {
        dispatch(
          snackbarSuccessHandler({
            title: T.translate("general.success"),
            html: T.translate(
              "edit_sponsor.pages_tab.customized_page_unarchived"
            )
          })
        );
        const { term, showArchived, customizedPages } =
          getState().sponsorPagePagesListState;
        const { currentPage, perPage, order, orderDir } = customizedPages;
        return dispatch(
          getSponsorCustomizedPages(
            term,
            currentPage,
            perPage,
            order,
            orderDir,
            showArchived
          )
        );
      })
      .catch(() => {})
      .finally(() => dispatch(stopLoading()));
  };

export const deleteSponsorCustomizedPage =
  (pageId) => async (dispatch, getState) => {
    const { currentSummitState, currentSponsorState } = getState();
    const { currentSummit } = currentSummitState;
    const {
      entity: { id: sponsorId }
    } = currentSponsorState;
    const accessToken = await getAccessTokenSafely();
    const params = { access_token: accessToken };

    dispatch(startLoading());

    return deleteRequest(
      null,
      createAction(SPONSOR_CUSTOMIZED_PAGE_DELETED)({ pageId }),
      `${window.SPONSOR_PAGES_API_URL}/api/v1/summits/${currentSummit.id}/sponsors/${sponsorId}/sponsor-pages/${pageId}`,
      null,
      snackbarErrorHandler
    )(params)(dispatch)
      .then(() => {
        dispatch(
          snackbarSuccessHandler({
            title: T.translate("general.success"),
            html: T.translate("show_pages.page_delete_success")
          })
        );
      })
      .finally(() => {
        dispatch(stopLoading());
      });
  };

const normalizeSponsorCustomPage = (entity, summitTZ) => {
  const normalizedEntity = {
    ...entity,
    apply_to_all_add_ons: false
  };

  if (entity.allowed_add_ons.includes("all")) {
    normalizedEntity.apply_to_all_add_ons = true;
    normalizedEntity.allowed_add_ons = [];
  } else {
    normalizedEntity.allowed_add_ons = entity.allowed_add_ons.map((e) => e.id);
  }

  normalizedEntity.modules = normalizePageTemplateModules(
    entity.modules,
    summitTZ
  );

  return normalizedEntity;
};
